import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  AddEmployeesToAttendancePolicyDocument,
  AttendancePolicyEmployeesCollectionListDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import ModalComponent from 'modules/core/components/Modal'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import attendancePolicies from 'modules/attendance/store/collections/policies'
import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'

interface Props {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>
  policyEmployeesIds: number[]
  attendancePolicyId: number
  onClose: () => void
}

interface AddEmployeesFormValues {
  employees: EmployeeForSelectFragmentNoAccess[]
}

const AddEmployeesModal = ({
  policyEmployeesIds,
  setShowModal,
  attendancePolicyId,
  onClose,
}: Props) => {
  const [addEmployeesToAttendancePolicy] = useMutation(AddEmployeesToAttendancePolicyDocument, {
    extractorFn: (data) => data.attendanceMutations.addEmployeesAttendanceEmployeesPolicy,
    refetchQueries: [AttendancePolicyEmployeesCollectionListDocument],
  })

  const form = useForm<AddEmployeesFormValues>({
    defaultValues: {
      employees: [],
    },
    validate: (data) => {
      const errors: FormErrors<AddEmployeesFormValues> = {}

      if (data.employees.length === 0) {
        errors.employees = i18n.t('common.validations.empty')
      }
      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await addEmployeesToAttendancePolicy({
        variables: {
          policyId: attendancePolicyId,
          employeeIds: data.employees.map((e) => e.id),
        },
      })
      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success && handled.data?.employeesPolicy?.employeeIds) {
        onClose()
        await attendancePolicies.fetch()

        app.setFlash({
          type: 'correct',
          message: i18n.t('time_tracking.settings.employees.add.flash_messages.successful_update'),
          time: 2000,
        })
      }
      return handled
    },
  })

  return (
    <ModalComponent
      illustration="people-add"
      title={i18n.t('time_tracking.settings.employees.add.title')}
      description={i18n.t('time_tracking.settings.employees.add.description')}
      onSubmit={() => form.handleSubmit()}
      onClose={() => setShowModal(false)}
      disableFocusTrap
      footer={
        <Button disabled={false} submit>
          {i18n.t('time_tracking.settings.employees.add.cta')}
        </Button>
      }
    >
      <EmployeeSelector
        excludeIds={policyEmployeesIds}
        multiple
        value={form.values.employees}
        withAvatar
        excludeCurrentEmployee
        onSelect={(selectedEmployees) =>
          form.setValues({ ...form.values, employees: selectedEmployees })
        }
      />
    </ModalComponent>
  )
}

export default observer(AddEmployeesModal)
